import { State, Action, StateContext, Selector, Store, Select } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Injectable } from '@angular/core';

import { BannerService } from '../../shared/services/banner.service';
import { HelperService } from '../../../../src/app/services/helper/helper.service';
import { SaveState } from '../../models/commonModels';

import * as UtilisationOrderActions from './utilisation-order.action';
import { UtilisationOrderService } from './utilisation-order.service';
import { OrderService } from '../order/order.service';
import * as UtilisationOrderModels from '../../models/utilisation-order/utilisation-order';
import * as OrderStatusModels from '../../models/order/orderStatus';
import { UtilisationOrderSearchModel } from '../../models/utilisation-order/utilisation-orderSearch';
import { StorageService } from '../../shared/services/storage.service';

export interface UtilisationOrderStateModel {
    OrderStatus: OrderStatusModels.OrderStatusListModel;
    OrderList: UtilisationOrderModels.UtilisationOrderListModel;
    Order: UtilisationOrderModels.UtilisationOrderObjectModel;
    Search: UtilisationOrderSearchModel;
    Save: SaveState;
}

class DictionaryKeys {
  public static readonly OrderStatus = "UtilisationOrderStatus";
}


@State<UtilisationOrderStateModel>({
    name: 'utilisationOrderState',
    defaults: {
      OrderStatus: null,
      OrderList: null,
      Search: null,
      Save: null,
      Order: null
    }
  })

  @Injectable()

  export class UtilisationOrderState {

    constructor(
      private helperService: HelperService,
      private orderService: OrderService,
      private utilisationOrderService: UtilisationOrderService,
      private bannerService: BannerService,
      private storageService: StorageService
  ) { }


  @Selector()
  static OrderStatus(state: UtilisationOrderStateModel) {
    return state.OrderStatus;
  }

  @Selector()
  static OrderList(state: UtilisationOrderStateModel) {
    return state.OrderList;
  }

  @Selector()
  static Order(state: UtilisationOrderStateModel) {
    return state.Order;
  }

  @Selector()
  static SaveOrder(state: UtilisationOrderStateModel) {
    return state.Save;
  }

  @Selector()
  static OrderSearch(state: UtilisationOrderStateModel) {
    return state.Search;
  }

  @Action(UtilisationOrderActions.GetList)
  GetList(
    context: StateContext<UtilisationOrderStateModel>,
    action: UtilisationOrderActions.GetList
  ) {

    const errorMessage = 'Błąd pobierania listy zleceń utylizacji.';

    context.setState(
      patch({
        OrderList: null
      })
    );

    this.utilisationOrderService.getList(action.Search).subscribe((ret) => {
      if (ret.Success) {
        context.setState(
          patch({
            OrderList: ret
          })
        );

        return ret;

      } else {
        this.bannerService.error(ret.Message, errorMessage);
      }
    },
      (error) => {
        this.helperService.SaveException('OrderActions.GetList', error);

        this.bannerService.error(errorMessage);
      });
  }

  @Action(UtilisationOrderActions.SetSearch)
  SetSearch(
    context: StateContext<UtilisationOrderStateModel>,
    action: UtilisationOrderActions.SetSearch
  ) {

    action.Search.CurrentPage = 1;

    context.setState(
      patch({
        Search: action.Search
      }));

    context.dispatch(new UtilisationOrderActions.GetList(action.Search));
  }

  @Action(UtilisationOrderActions.SetListPage)
  SetListPage(
    context: StateContext<UtilisationOrderStateModel>,
    action: UtilisationOrderActions.SetListPage
  ) {

    context.setState(
      patch({
        Search: action.Search
      }));

    context.dispatch(new UtilisationOrderActions.GetList(action.Search));
  }

  @Action(UtilisationOrderActions.ResetSearch)
  ResetSearch(
    context: StateContext<UtilisationOrderStateModel>
  ) {

    const state = context.getState();

    const search = new UtilisationOrderSearchModel();

    search.ItemsPerPage = state.Search.ItemsPerPage;

    context.setState(
      patch({
        Search: search
      }));

    context.dispatch(new UtilisationOrderActions.GetList(search));
  }

  @Action(UtilisationOrderActions.GetStatusDictionary)
  GetStatusDictionary(
    context: StateContext<UtilisationOrderStateModel>
  ) {

    const errorMessage = 'Błąd pobierania statusów zleceń utylizacji.';

    let storageKey = DictionaryKeys.OrderStatus;

    context.setState(
      patch({
        OrderStatus: this.storageService.getItemJSON(storageKey)
      })
    );

    const state = context.getState();

    if (!state.OrderStatus) {
      this.utilisationOrderService.getStatusDictionary().subscribe((ret) => {
        if (ret.Success) {

          context.setState(
            patch({
              OrderStatus: ret
            })
          );

          this.storageService.setItemJSON(storageKey, ret);

        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },
        (error) => {
          this.helperService.SaveException('UtilisationOrderActions.GetStatusDictionary', error);

          this.bannerService.error(errorMessage);
        });
    }
  }

  @Action(UtilisationOrderActions.DisposeSearch)
  DisposeSearch(
      context: StateContext<UtilisationOrderStateModel>
    ) {

      context.setState(
        patch({
          Search: null
        }));
  }

  @Action(UtilisationOrderActions.Set)
  Set(
    context: StateContext<UtilisationOrderStateModel>,
    action: UtilisationOrderActions.Set
  ) {
    const errorMessage = 'Błąd zapisu zmian zlecenia utylizacji opon.';

    this.utilisationOrderService.set(action.Order).subscribe(
      ret => {

        if (ret.Success) {
          this.bannerService.success('Zapisano zmiany do zlecenia utylizacji opon ' + ret.Item.Number + '.');

          context.setState(
            patch({
              Order: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('UtilisationOrderActions.Set', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }

  @Action(UtilisationOrderActions.Get)
  Get(
    context: StateContext<UtilisationOrderStateModel>,
    action: UtilisationOrderActions.Get
  ) {
    const errorMessage = 'Błąd odczytu szczegółów zlecenia zamówienia opon.';

    this.utilisationOrderService.get(action.OrderId).subscribe(
      ret => {

        if (ret.Success) {

          context.setState(
            patch({
              Order: ret
            })
          );
        } else {
          this.bannerService.error(ret.Message, errorMessage);
        }
      },

      (error) => {

        this.helperService.SaveException('UtilisationOrderActions.Get', error);

        this.bannerService.error(errorMessage);
      },
      () => { });
  }
}
